import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect } from "react";
import { useMap } from "react-map-gl/mapbox";
import * as sunCalc from "suncalc";
import { Box } from "@gemini/core";
import { Slider, Toggle } from "@gemini/ui";
import { useI18n } from "@/shared/hooks";
import { THREE_D_ZOOM, useMapContext } from "@/domains/map";
import { getLightProperties } from "../../../utils/sunlightProperties";
import { formatTime } from "../../../utils/formatTime";
export const MapSunlight = () => {
    const { t } = useI18n();
    const { current: map } = useMap();
    const { layers, setLayers, time, setTime, initialLights, style } = useMapContext();
    const handleToggleChange = (e) => {
        setLayers(Object.assign(Object.assign({}, layers), { sunlight: e }));
        // @ts-ignore
        map.easeTo({
            zoom: THREE_D_ZOOM,
            duration: 2000,
        });
    };
    const updateSunlights = useCallback((date) => {
        if (!date || !map)
            return;
        const sunPosition = sunCalc.getPosition(date, map.getCenter().lat, map.getCenter().lng);
        const azimuthInDegrees = 180 + (sunPosition.azimuth * 180) / Math.PI; // convert azimuth radians to degress with 180deg offset // https://github.com/mourner/suncalc
        const direction = [
            azimuthInDegrees, // light's direction - horizontal angle
            Math.min(90, Math.max(0, 90 - sunPosition.altitude * (180 / Math.PI))), // altitude, vertical angle
        ];
        const lightProperties = getLightProperties(azimuthInDegrees);
        const lightLayers = [
            {
                type: "ambient",
                id: "ambient-light",
                properties: {
                    intensity: lightProperties.ambient.intensity,
                    color: lightProperties.ambient.color,
                },
            },
            {
                type: "directional",
                id: "directional-light",
                properties: {
                    intensity: lightProperties.directional.intensity,
                    direction: direction,
                    "cast-shadows": true,
                    "shadow-intensity": 0.8,
                    color: lightProperties.directional.color,
                },
            },
        ];
        map.setLights(lightLayers);
    }, [map]);
    const handleSliderChange = useCallback((value) => {
        const newTime = value ? Number(value) : time;
        setTime(newTime);
        const date = new Date();
        date.setHours(0, 0, 0, 0); // reset to today at midnight
        date.setMinutes(newTime); // add minutes since midnight
        updateSunlights(date);
    }, [setTime, time, updateSunlights]);
    useEffect(() => {
        if (!map)
            return;
        if (layers.sunlight === false) {
            map.setLights(initialLights);
        }
        else {
            handleSliderChange(undefined);
        }
    }, [handleSliderChange, initialLights, layers.sunlight, map]);
    return (_jsxs(_Fragment, { children: [_jsx(Box, { marginBottom: "spacing.16", children: _jsx(Toggle, { hasNecessityIndicator: false, isSelected: layers.sunlight, onChange: handleToggleChange, isDisabled: style === "standard-satellite", children: t("location.map.modal.sunlight.toggle.label") }) }), layers.sunlight && (_jsxs(Box, { display: "flex", flexDirection: "column", borderColor: "color.border.light.default", borderStyle: "solid", borderWidth: "borderWidth.1", borderRadius: "radius.8", paddingHorizontal: "spacing.16", paddingVertical: "spacing.12", children: [_jsxs(Box, { display: "flex", justifyContent: "space-between", children: [_jsx(Box, { as: "span", typography: "typography.body.14.regular", children: t("location.map.modal.sunlight.slider.label") }), _jsx(Box, { as: "span", typography: "typography.body.14.regular", children: formatTime(time) })] }), _jsx(Slider, { label: {
                            content: t("location.map.modal.sunlight.slider.label"),
                            isHidden: true,
                        }, min: 0, max: 1439, value: time, onChange: handleSliderChange, shouldDisplayCurrentValue: false, hasNecessityIndicator: false }), _jsxs(Box, { display: "flex", justifyContent: "space-between", children: [_jsx(Box, { as: "span", typography: "typography.body.12.regular", color: "color.content.light.default", children: t("location.map.modal.sunlight.slider.min_label") }), _jsx(Box, { as: "span", typography: "typography.body.12.regular", color: "color.content.light.default", children: t("location.map.modal.sunlight.slider.max_label") })] })] }))] }));
};
